import { imageWrapperPreserveRatio } from '@helpers/aspectRatio';
import brand from '@helpers/brand';
import { cssVar, setCssVar } from '@helpers/cssVar';
import { Device, from, until } from '@helpers/media';
import LinkButtonStyles from '@stories/Components/Buttons/LinkButton/LinkButton.styles';
import HeadingStyles from '@stories/Components/Global/Typography/Heading.styles';
import RichTextStyles from '@stories/Widgets/RichText/RichText.styles';
import { m } from 'framer-motion';
import styled from 'styled-components';
const Container = styled(m.article) `
  display: grid;

  @media ${until(Device.Tablet)} {
    gap: 58px 10px;
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
  }

  @media ${from(Device.Tablet)} {
    gap: 30px;
    grid-template-columns: repeat(12, 1fr);
  }

  .theme-hh & {
    ${setCssVar('accentColor', brand.hortHouse.green)};
    ${setCssVar('accentColorAlt', brand.hortHouse.greyMid)};
    ${setCssVar('fgColor', brand.white)};

    @media ${until(Device.Tablet)} {
      gap: 10px;
    }
  }

  .theme-rss & {
    ${setCssVar('accentColor', brand.rss.green)};
    ${setCssVar('accentColorAlt', brand.greyLight)};
    ${setCssVar('fgColor', brand.white)};

    @media ${until(Device.Tablet)} {
      gap: 10px;
    }
  }
`;
const ContentWrapper = styled.div `
  @media ${until(Device.Tablet)} {
    grid-row: 2;

    .theme-rss & {
      grid-row: 1;
      padding: 60px 0 30px;
    }
  }

  @media ${from(Device.Tablet)} {
    grid-column: 1 / span 6;
    padding: 16px 0 148px;

    .theme-rss & {
      padding: 30px 0 100px;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }

    .theme-hh & {
      padding-bottom: 60px;
    }
  }

  @media ${from(Device.TabletLarge)} {
    grid-column: 1 / span 6;
  }

  @media ${from(Device.Desktop)} {
    .theme-hh & {
      padding-bottom: 100px;
    }
  }
`;
const Title = styled.h1 `
  color: ${cssVar('fgColor')};
  ${HeadingStyles.H1Styles};
  margin: 0 0 24px;

  .theme-rss & {
    @media ${from(Device.TabletLarge)} {
      font-size: 50px;
      line-height: 60px;
    }
  }
`;
const Content = styled.div `
  ${RichTextStyles.contentStyles};
  margin: 0 0 24px;
`;
const CtaWrapper = styled.div `
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  @media ${from(Device.Tablet)} {
    flex-direction: row;
    ${LinkButtonStyles.Button} {
      padding-left: 12px;
      padding-right: 12px;
    }
  }

  @media ${from(Device.TabletLarge)} {
    ${LinkButtonStyles.Button} {
      margin-right: 16px;
      margin-bottom: 10px;
      padding-left: 24px;
      padding-right: 24px;

      &:last-child {
        margin-right: 0;
      }
    }
  }
`;
const ImageWrapper = styled.div `
  .theme-rss & {
    position: relative;
  }

  @media ${until(Device.Tablet)} {
    grid-row: 1;
    margin: 0 auto;
    min-width: 194px;
    width: 50%;

    .theme-hta & {
      width: 100%;
      padding-left: 0;
    }

    .theme-hh & {
      width: 75%;
    }

    .theme-rss & {
      grid-row: 2;
      width: 100%;
    }
  }

  @media ${from(Device.Tablet)} {
    grid-column: 7 / span 6;
  }

  @media ${from(Device.TabletLarge)} {
    grid-column: 8 / span 5;

    .theme-hta & {
      grid-column: 7 / span 6;
      padding-left: 20px;
    }
  }
`;
const ImageWrapperInner = styled.div `
  position: relative;
  height: 0;
  padding-bottom: 100%;

  @media ${from(Device.TabletLarge)} {
    .theme-hh & {
      padding-bottom: 80%;
    }
  }

  .theme-hta & {
    ${imageWrapperPreserveRatio(801, 522)};
    aspect-ratio: 801 / 522;
    border-radius: 47px;
    border: 4px solid ${brand.htaRebrand.greenBright};
    overflow: hidden;
    width: 100%;    
  }

  @media ${until(Device.Tablet)} {
    .theme-rss & {
      height: 200px;
      padding-bottom: 0;
    }

    .theme-hta & {
      border: 2px solid ${brand.htaRebrand.greenBright};
      border-radius: 14px;
    }
  }
`;
const Image = styled.img `
  position: absolute;
  top: 0;
  left: 0;
  display: block;
  border-radius: 0;
  height: 100%;
  object-fit: cover;
  object-position: center center;
  width: 100%;
  z-index: 2;

  .theme-hh & {
    border-radius: 55px;
    height: 75%;
    top: 50%;
    transform: translateY(-50%);

    @media ${until(Device.Tablet)} {
      top: 50%;
      max-height: 80%;
      border-radius: 12px;
    }

    @media ${from(Device.TabletLarge)} {
      height: 75%;
      max-height: 400px;
    }

    @media ${from(Device.Desktop)} {
    }
  }
`;
const HHGraphicWrapper = styled.div `
  display: none;

  .theme-hh & {
    display: block;
  }
`;
const RssGraphicWrapper = styled.div `
  display: none;

  .theme-rss & {
    display: block;
  }
`;
const Graphic1 = styled.span `
  position: absolute;
  display: block;
  color: ${cssVar('accentColor')};
  transform: rotate(12deg);
  width: 372px;
  z-index: 1;

  .theme-hh & {
    transform: unset;
    right: 0;
    top: 0;
  }

  .theme-rss & {
    transform: unset;
    left: 0;
    bottom: 0;
  }

  @media ${until(Device.Tablet)} {
    top: 4px;
    right: -22px;
    width: 120px;

    .theme-hh & {
      width: 140px;
    }

    .theme-rss & {
      transform: unset;
      top: auto;
      left: 50%;
      bottom: -40px;
      transform: translateX(-50%);
      width: 340px;
      height: 210px;
    }
  }

  @media ${from(Device.Tablet)} {
    top: 15px;
    right: -70px;
    width: 372px;

    .theme-hh & {
      width: 200px;
      top: -15px;
    }

    .theme-rss & {
      top: auto;
      right: auto;
      bottom: -70px;
      width: 952px;
    }
  }

  @media ${from(Device.DesktopSmall)} {
    .theme-hh & {
      width: 250px;
    }
  }
`;
const Graphic2 = styled.span `
  position: absolute;
  bottom: -60px;
  left: -16px;
  display: block;
  color: ${cssVar('accentColorAlt')};
  transform: rotate(-136deg);
  width: 285px;
  z-index: 1;

  .theme-hh & {
    transform: unset;
    left: 0;
    bottom: 15px;
  }

  @media ${until(Device.Tablet)} {
    bottom: -20px;
    left: -5px;
    width: 91px;

    .theme-hh & {
      width: 100px;
    }
  }

  @media ${from(Device.Tablet)} {
    bottom: -60px;
    left: -16px;
    width: 285px;

    .theme-hh & {
      width: 150px;
    }
  }

  @media ${from(Device.DesktopSmall)} {
    .theme-hh & {
      width: 200px;
      bottom: 0;
    }
  }
`;
export default {
    Container,
    Content,
    ContentWrapper,
    CtaWrapper,
    Graphic1,
    Graphic2,
    Image,
    ImageWrapper,
    ImageWrapperInner,
    Title,
    HHGraphicWrapper,
    RssGraphicWrapper,
};
